// Base Provider class — capability management, display helpers, default
// seat-selection logic and the abstract upstream contract. Pure helpers
// (type coercion, money, ISO durations, shape builders) live in
// `./helpers.js` and are imported directly by concrete providers — no
// `this.as_string(…)` style proxying on the class.
//
// See providers/types.js for the FlightOffer / Amenity / Selections
// typedefs that every concrete `normalize()` produces.

import { is_adult } from '@liveflight/utilities/travelers';
import { as_compare_string } from './helpers';

// True when every fareDetailsBySegment entry on an upsell offer carries
// the same brandedFare — i.e. the upsell applies cleanly to the whole
// journey. Mixed-brand upsells are dropped by filter_visible_upsells
// because the UI can't render a single brand label for them.
const is_homogeneous_brand = (upsell) => {
  const { travelerPricings = [] } = upsell || {};
  const { fareDetailsBySegment = [] } = travelerPricings[0] || {};
  if (fareDetailsBySegment.length === 0) {
    return true;
  }
  const first_brand = fareDetailsBySegment[0].brandedFare;
  return fareDetailsBySegment.every(
    (fd) => fd && fd.brandedFare === first_brand
  );
};

export default class Provider {
  static DEFAULT_CAPABILITIES = {};

  constructor({ providerId, capabilities = {} } = {}) {
    if (typeof providerId !== 'string' || providerId.length === 0) {
      throw new Error('[Provider] providerId is required and must be a string');
    }
    this.providerId = providerId;
    this.version = '';
    this.capabilities = {
      ...(this.constructor.DEFAULT_CAPABILITIES || {}),
      ...(capabilities || {}),
    };
    // Internal lookup caches — populated as a side effect of
    // get_search_results() and consumed by display_carrier / display_airport.
    this._carriers = {};
    this._airports = {};
  }

  // ─── Capabilities ───────────────────────────────────────────────────────

  has_capability(name) {
    return this.capabilities[name] === true;
  }

  get_capabilities() {
    return { ...this.capabilities };
  }

  // ─── Display helpers ────────────────────────────────────────────────────
  // Each provider caches IATA → name lookups while parsing a response inside
  // its own dictionaries module. Components call these to render human-
  // readable names without touching any dictionary directly. Return '' when
  // no mapping is known — callers decide whether to render the IATA code as
  // a fallback.

  display_carrier(code) {
    if (!code) {
      return '';
    }
    return this._carriers[code] || '';
  }

  display_airport(code) {
    if (!code) {
      return '';
    }
    const entry = this._airports[code];
    if (!entry) {
      return '';
    }
    return entry.name || '';
  }

  display_location(code) {
    if (!code) {
      return '';
    }
    const entry = this._airports[code];
    if (!entry) {
      return '';
    }
    return entry.cityName || '';
  }

  // Full dictionary read-outs — returned as fresh copies so callers can
  // safely iterate / mutate their consumer-side state. Useful for UI code
  // that needs the whole map in one shot (e.g. seat-map header expecting
  // a `locations` prop), where calling display_* per-code would be noisy.

  get_known_carriers() {
    return { ...this._carriers };
  }

  get_known_locations() {
    const copy = {};
    Object.keys(this._airports).forEach((iata) => {
      copy[iata] = { ...this._airports[iata] };
    });
    return copy;
  }

  // Count of ADULT travelers on the given offer. Each provider can
  // override to map its taxonomy to adults — the default uses is_adult,
  // which resolves traveler_type / travelerType / type for any
  // normalized offer.
  count_adults(flight_offer) {
    const { travelerPricings = [] } = flight_offer || {};
    return travelerPricings.filter(is_adult).length;
  }

  // ─── Contract — concrete providers MUST override ────────────────────────

  validate(/* raw */) {
    throw new Error(`${this.constructor.name}.validate(raw) not implemented`);
  }

  normalize(/* raw */) {
    throw new Error(`${this.constructor.name}.normalize(raw) not implemented`);
  }

  // Search — every provider's single public entry point for flight discovery.
  // Takes the same generic search payload (the request the UI builds in
  // executeSearch) and returns a Promise resolving to a normalized
  // FlightOffer[]. Internally hits service_handler for the right service
  // name + body shape and normalizes the response. No raw envelope leaks.
  search(/* payload */) {
    return Promise.reject(
      new Error(`${this.constructor.name}.search not implemented`)
    );
  }

  // No-results suggestions — capability-gated probe for alternative offers
  // when the primary search comes back empty. Resolves to two parallel
  // buckets of normalized FlightOffer[]:
  //   by_radius      — same dates, widened origin/destination radius
  //   by_time_window — same airports, widened date window
  // Providers without `searchSuggestions` resolve to empty buckets so the
  // dispatcher can call unconditionally.
  search_suggestion(payload) {
    if (!this.has_capability('searchSuggestions')) {
      return Promise.resolve({ by_radius: [], by_time_window: [] });
    }
    return this.fetch_search_suggestion(payload);
  }

  fetch_search_suggestion(/* payload */) {
    return Promise.reject(
      new Error(
        `${this.constructor.name}.fetch_search_suggestion not implemented`
      )
    );
  }

  // ─── Resolvable upstream calls ──────────────────────────────────────────
  // UI components talk to these provider-agnostic methods instead of wiring
  // service_handler themselves. Capability-gated public methods silently
  // resolve to a null default when the provider does not advertise the
  // capability. The fetch_* subclass hooks are only reached when the
  // capability IS on, so they throw loudly when left unimplemented — that's
  // a misconfigured provider.

  // Reprice / enrich the selected offer. Always available. Returns a
  // normalised FlightOffer reflecting the post-price data.
  get_offer_details(/* flight_offer */) {
    return Promise.reject(
      new Error(`${this.constructor.name}.get_offer_details not implemented`)
    );
  }

  // Returns the fully-normalized seatmap response (SeatMapResponse in
  // providers/types.js) — validation, per-deck mapping, and per-deck
  // disabled-gating are all baked in by the provider's fetch_seatmap.
  // Consumer reads `mappedDecks` / `isValid` / `canAllPassengersSelectOneSeat`
  // directly without any further iteration.
  //
  // options:
  //   areInfantsPresent — provider must flag decks without infant-suitable
  //                       seats as disabled.
  //   allowMalformed    — keep malformed decks in the map (debugging).
  get_seatmap(flight_offer, options) {
    if (!this.has_capability('seatSelection')) {
      return Promise.resolve(null);
    }
    return this.fetch_seatmap(flight_offer, options);
  }

  // Resolves to a list of normalized FlightOffer alternatives (branded-fare
  // variants of the current offer) or null when the provider doesn't
  // advertise brandedFareUpsell. UI picks one and commits it directly —
  // no raw envelope ever reaches the store.
  get_upsell(flight_offer) {
    if (!this.has_capability('brandedFareUpsell')) {
      return Promise.resolve(null);
    }
    return this.fetch_upsell(flight_offer);
  }

  // ─── Fare choice — branded-fare upsell selection ───────────────────────
  // Every pure helper below operates on the canonical normalized FlightOffer
  // shape. The Amadeus provider overrides them to read the brandedFare
  // string on fareDetailsBySegment[0]; other providers that use a different
  // grouping identifier should override `get_branded_fare` and the rest
  // will fall in line. Kyte doesn't advertise brandedFareUpsell so the
  // public methods short-circuit to empty defaults.

  // Identifier used to group upsell variants. Default: the brandedFare
  // field on the first fare-detail, which matches the Amadeus convention.
  get_branded_fare(flight_offer) {
    const { travelerPricings = [] } = flight_offer || {};
    const { fareDetailsBySegment = [] } = travelerPricings[0] || {};
    const { brandedFare = '' } = fareDetailsBySegment[0] || {};
    return brandedFare;
  }

  get_fare_basis_key(flight_offer) {
    const { travelerPricings = [] } = flight_offer || {};
    return travelerPricings
      .flatMap((pricing) => pricing.fareDetailsBySegment || [])
      .flatMap((fare) => fare.fareBasis)
      .join(':');
  }

  find_selected_upsell(upsells, current_flight_offer) {
    if (!this.has_capability('brandedFareUpsell')) {
      return current_flight_offer;
    }
    const current_brand = this.get_branded_fare(current_flight_offer);
    const list = Array.isArray(upsells) ? upsells : [];

    const match_methods = [
      (u) =>
        as_compare_string(this.get_branded_fare(u)) ===
        as_compare_string(current_brand),

      (u) =>
        this.get_fare_basis_key(u) ===
        this.get_fare_basis_key(current_flight_offer),
    ];
    for (let index = 0; index < match_methods.length; index++) {
      const method = match_methods[index];
      const found = list.find(method);
      if (found) {
        return found;
      }
    }

    return current_flight_offer;
  }

  // Visible upsells = default offer + up to (max_count - 1) additional
  // branded variants that
  //   - do not duplicate the default's brand,
  //   - carry the same brand on every segment (homogeneous),
  //   - list the same number of passengers as the current UI expects.
  // Order is preserved from upstream so providers that pre-sort (Amadeus
  // by price, Kyte bundles by tier) keep their intent. When no default
  // is supplied we fall back to a plain slice so callers that just want
  // "the first N upsells" still work.
  filter_visible_upsells(params = {}) {
    if (!this.has_capability('brandedFareUpsell')) {
      return [];
    }
    const { upsells, default_offer, traveler_count, max_count } = params;
    const list = Array.isArray(upsells) ? upsells : [];
    const cap = Math.max(0, max_count);
    if (!default_offer) {
      return list.slice(0, cap);
    }
    const default_brand = this.get_branded_fare(default_offer);
    const extras = list
      .filter((upsell) => {
        if (this.get_branded_fare(upsell) === default_brand) {
          return false;
        }
        if (!is_homogeneous_brand(upsell)) {
          return false;
        }
        const { travelerPricings = [] } = upsell || {};
        return travelerPricings.length === traveler_count;
      })
      .slice(0, Math.max(0, cap - 1));
    return [default_offer, ...extras];
  }

  // The "pick" action. Resolves to the normalized offer that should be
  // committed to the store. Provider-gated so callers don't need to know
  // whether brandedFareUpsell is on. Default behaviour is pass-through;
  // Amadeus overrides via fetch_upsell_choice — a future provider that
  // needs an upstream confirm call can override there too.
  choose_upsell(flight_offer) {
    if (!this.has_capability('brandedFareUpsell')) {
      return Promise.resolve(flight_offer || null);
    }
    return this.fetch_upsell_choice(flight_offer);
  }

  fetch_upsell_choice(flight_offer) {
    return Promise.resolve(flight_offer || null);
  }

  get_extra_bags(flight_offer) {
    if (!this.has_capability('extraBags')) {
      return Promise.resolve(null);
    }
    return this.fetch_extra_bags(flight_offer);
  }

  // Apply the FlexibleBagsState selection onto the offer and return the
  // updated offer wrapped in a Promise. Providers that reprice on bag
  // changes (Amadeus → flightOffersPrice) resolve with the repriced
  // normalized offer; providers without a reprice step (Kyte — selections
  // sit until book-time) resolve with the offer mutated in-memory.
  // Capability-gated so UI code can call unconditionally.
  add_extra_bags(flight_offer, flexible_bags) {
    if (!this.has_capability('extraBags')) {
      return Promise.resolve(flight_offer);
    }
    return this.do_add_extra_bags(flight_offer, flexible_bags);
  }

  // Read the bags currently attached to an offer for display at confirm /
  // checkout. Default walks every traveler × segment fareDetail and emits
  // one entry per chargeableCheckedBags found — works for any provider
  // that conforms to the canonical FlightOffer shape (both today).
  // Override only if a provider needs different lookup semantics.
  read_extra_bags(flight_offer) {
    if (!this.has_capability('extraBags')) {
      return [];
    }
    return this.do_read_extra_bags(flight_offer);
  }

  do_read_extra_bags(flight_offer) {
    const { travelerPricings = [] } = flight_offer || {};
    return travelerPricings.reduce((acc, pricing) => {
      const { travelerId, fareDetailsBySegment = [] } = pricing;
      return fareDetailsBySegment.reduce((inner, fare) => {
        const { additionalServices = [], segmentId } = fare || {};
        const list = Array.isArray(additionalServices)
          ? additionalServices
          : [];
        const bags = list.filter((entry) => entry && entry.type === 'bag');
        if (!bags.length) return inner;
        return inner.concat(
          bags.map((entry) => ({ travelerId, segmentId, bag: entry }))
        );
      }, acc);
    }, []);
  }

  // ─── Extra / flexible bag operations on the offer ──────────────────────
  // Capability-gated defaults so UI code can ask unconditionally. Providers
  // that advertise flexibleBags must override with real behaviour (Amadeus
  // does via ./amadeus/bags.js). Providers that don't advertise the
  // capability get the no-op shapes below.

  add_charged_bags(flight_offer /*, flexible_bags */) {
    if (!this.has_capability('flexibleBags')) {
      return flight_offer;
    }
    throw new Error(
      `${this.constructor.name}.add_charged_bags not implemented`
    );
  }

  do_add_extra_bags(/* flight_offer, flexible_bags */) {
    return Promise.reject(
      new Error(`${this.constructor.name}.do_add_extra_bags not implemented`)
    );
  }

  has_chargeable_bags(/* flight_offer, traveler_id */) {
    return false;
  }

  has_included_bags(/* flight_offer, traveler_id */) {
    return false;
  }

  get_bags(/* flight_offer, itinerary_index */) {
    return {
      extra: false,
      travelers: [],
      cabin: 0,
      included: 0,
      chargeable: 0,
      total: 0,
    };
  }

  // ─── Seat selection — shared contract, provider-owned reprice ───────────
  //
  // Canonical shapes used by these methods — see providers/types.js for the
  // full typedefs. Every provider talks to the UI through them so the
  // seat-selection page stays provider-agnostic.
  //
  // Contract:
  //   apply_seats           — async, reprice upstream, return normalized offer
  //   read_seats            — sync,  pull current selections from an offer
  //   validate_seats_priced — sync,  confirm repriced offer reflects selections

  apply_seats(flight_offer, selections) {
    if (!this.has_capability('seatSelection')) {
      return Promise.resolve(null);
    }
    return this.fetch_seats_applied(flight_offer, selections);
  }

  read_seats(flight_offer, segment_id, seat_options_for_segment = []) {
    if (!this.has_capability('seatSelection')) {
      return {};
    }
    return this.do_read_seats(
      flight_offer,
      segment_id,
      seat_options_for_segment
    );
  }

  validate_seats_priced(flight_offer, selections, priced_flight_offer) {
    if (!this.has_capability('seatSelection')) {
      return true;
    }
    return this.do_validate_seats_priced(
      flight_offer,
      selections,
      priced_flight_offer
    );
  }

  // Does this offer require an explicit seat pick for every non-held-infant
  // traveler before the booking can move forward? Each provider answers
  // from its own corner of the fare:
  //   Amadeus — Ryanair carrier code + non-Basic brandedFare
  //   Kyte    — TBD (different field on the LCC fare envelope)
  // Default false so providers without the concept never block the flow.
  seats_are_mandatory(/* flight_offer */) {
    return false;
  }

  // Default read — works for any provider whose normalize() preserves
  // chargeableSeatNumber on fareDetailsBySegment[].additionalServices.
  // Returns { [travelerId]: SeatSelection | string }:
  //   - with a supplied seat-option list: hydrates to the full SeatSelection
  //     or drops the entry if the seat is no longer available for the
  //     traveler
  //   - without: returns the raw seat-number string — enough to restore UI
  //     state on later visits
  do_read_seats(flight_offer, segment_id, seat_options_for_segment) {
    const { travelerPricings } = flight_offer || {};
    if (!Array.isArray(travelerPricings)) {
      return {};
    }
    const options = Array.isArray(seat_options_for_segment)
      ? seat_options_for_segment
      : [];
    const seat_index = {};
    options.forEach((seat) => {
      if (seat && seat.number) {
        seat_index[seat.number] = seat;
      }
    });
    const selections = {};
    travelerPricings.forEach((pricing) => {
      const { travelerId: traveler_id, fareDetailsBySegment = [] } =
        pricing || {};
      fareDetailsBySegment
        .filter((fd) => fd && fd.segmentId === segment_id)
        .forEach((fd) => {
          const { additionalServices = [] } = fd || {};
          const list = Array.isArray(additionalServices)
            ? additionalServices
            : [];
          const seat_entry = list.find((entry) => {
            const { type = '', seatNumber = '' } = entry || {};
            return type === 'seat' && !!seatNumber;
          });
          if (!seat_entry) return;
          const seat_number = seat_entry.seatNumber;
          if (options.length === 0) {
            selections[traveler_id] = seat_number;
            return;
          }
          const seat = seat_index[seat_number];
          if (seat && this.is_seat_available_for_passenger(seat, traveler_id)) {
            selections[traveler_id] = seat;
          }
        });
    });
    return selections;
  }

  // Default validation — an upstream reprice is considered correct when it
  // carries a SEATS line in price.additionalServices AND every segment that
  // had selections shows at least that many chargeableSeatNumber entries in
  // the repriced offer. Providers with a different reprice surface should
  // override.
  do_validate_seats_priced(_flight_offer, selections, priced_flight_offer) {
    if (!priced_flight_offer) {
      return false;
    }
    const price = priced_flight_offer.price || {};
    const additional_services = Array.isArray(price.additionalServices)
      ? price.additionalServices
      : [];
    if (additional_services.findIndex((s) => s && s.type === 'SEATS') < 0) {
      return false;
    }
    const safe = selections || {};
    return Object.keys(safe).every((segment_id) => {
      const expected = Object.keys(safe[segment_id] || {}).length;
      if (!expected) {
        return true;
      }
      const found = Object.keys(
        this.do_read_seats(priced_flight_offer, segment_id, [])
      ).length;
      return found >= expected;
    });
  }

  // Shared availability check — canonical SeatSelection.travelerPricing.
  is_seat_available_for_passenger(seat, traveler_id) {
    const { travelerPricing = [] } = seat || {};
    const pricings = Array.isArray(travelerPricing) ? travelerPricing : [];
    const pricing = pricings.find((p) => p && p.travelerId === traveler_id);
    if (!pricing) {
      return false;
    }
    return (
      String(pricing.seatAvailabilityStatus || '').toUpperCase() === 'AVAILABLE'
    );
  }

  // Subclass hooks — only reached when the matching capability is on.
  // Return rejected Promises (not synchronous throws) so callers using
  // .then(success, error) / .catch get the failure through the normal
  // Promise channel instead of an uncaught exception.

  fetch_seatmap(/* flight_offer */) {
    return Promise.reject(
      new Error(`${this.constructor.name}.fetch_seatmap not implemented`)
    );
  }

  fetch_upsell(/* flight_offer */) {
    return Promise.reject(
      new Error(`${this.constructor.name}.fetch_upsell not implemented`)
    );
  }

  fetch_extra_bags(/* flight_offer */) {
    return Promise.reject(
      new Error(`${this.constructor.name}.fetch_extra_bags not implemented`)
    );
  }

  fetch_seats_applied(/* flight_offer, selections */) {
    return Promise.reject(
      new Error(`${this.constructor.name}.fetch_seats_applied not implemented`)
    );
  }

  // ─── Ancillaries-as-step flow (capability: ancillariesAsStep) ───────────
  // Providers that gather ancillaries on a separate post-detail page must
  // implement these three. Default is pass-through for providers that
  // don't advertise the capability so callers can ask unconditionally.
  //
  // Contract:
  //   book_held         — async, create a held booking (no payment),
  //                        return { bookingId, expiresAt, flight_offer }.
  //   fetch_ancillaries — async, list every ancillary (bags / seats /
  //                        sportsEquipment / service / …) for a held
  //                        booking. Object keyed by category — empty
  //                        categories are dropped.
  //   book_ancillaries  — async, commit the page's selections object onto
  //                        the held booking and return the repriced offer.

  book_held(flight_offer /*, traveler_details, contact_details */) {
    if (!this.has_capability('ancillariesAsStep')) {
      return Promise.resolve({ bookingId: '', expiresAt: '', flight_offer });
    }
    return Promise.reject(
      new Error(`${this.constructor.name}.book_held not implemented`)
    );
  }

  retrieve(/* booking_id */) {
    if (!this.has_capability('ancillariesAsStep')) {
      return Promise.resolve({ booking: null, expiresAt: '' });
    }
    return Promise.reject(
      new Error(`${this.constructor.name}.retrieve not implemented`)
    );
  }

  fetch_ancillaries(/* booking_id */) {
    if (!this.has_capability('ancillariesAsStep')) {
      return Promise.resolve({});
    }
    return Promise.reject(
      new Error(`${this.constructor.name}.fetch_ancillaries not implemented`)
    );
  }

  book_ancillaries(flight_offer /*, booking_id, selections */) {
    if (!this.has_capability('ancillariesAsStep')) {
      return Promise.resolve({ flight_offer, expiresAt: '' });
    }
    return Promise.reject(
      new Error(`${this.constructor.name}.book_ancillaries not implemented`)
    );
  }

  // ─── Core booking operations ────────────────────────────────────────────
  // Every production provider must implement these. Kept abstract so a
  // misconfigured provider fails at checkout time — but via a rejected
  // Promise so the checkout error handler runs and the UI unwinds cleanly
  // instead of hanging in a loading state from an uncaught synchronous throw.

  book(/* body */) {
    return Promise.reject(
      new Error(`${this.constructor.name}.book not implemented`)
    );
  }

  get_confirmation(/* request */) {
    return Promise.reject(
      new Error(`${this.constructor.name}.get_confirmation not implemented`)
    );
  }
}
